var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from 'react';
import { React } from 'libs/react';
import { Spinner } from 'components/Spinner';
import { destroyMewsCheckout, loadMewsCheckout, mewsLanguageCode } from 'libs/mews/checkout';
import './StoreMewsCheckout.scss';
const CONTAINER_ID = 'store-mews-checkout';
/** Mews may list a payment a moment after the checkout reports it. */
const CONFIRM_ATTEMPTS = 5;
const CONFIRM_DELAY_MS = 2000;
function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}
function post(path, body) {
    return __awaiter(this, void 0, void 0, function* () {
        const res = yield fetch(process.env.API_URL_ROOT + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body !== null && body !== void 0 ? body : {})
        });
        const data = yield res.json().catch(() => ({}));
        return { res, data };
    });
}
/**
 * Store payment with Mews Payments (the partner's property, else the
 * account's). The server creates a payment request for the exact amount; the
 * Mews checkout is loaded with it, and the payment is verified by the server
 * before the order is confirmed.
 *
 * @component StoreMewsCheckout
 * @since 1.0.0
 */
export function StoreMewsCheckout(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { deal, checkoutSessionKey, language, contactValues } = props;
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState(null);
    const onPaidRef = useRef(props.onPaid);
    onPaidRef.current = props.onPaid;
    const onOutOfStockRef = useRef(props.onOutOfStock);
    onOutOfStockRef.current = props.onOutOfStock;
    const onProductInactiveRef = useRef(props.onProductInactive);
    onProductInactiveRef.current = props.onProductInactive;
    const onPromoCodeInvalidRef = useRef(props.onPromoCodeInvalid);
    onPromoCodeInvalidRef.current = props.onPromoCodeInvalid;
    const contactValuesRef = useRef(contactValues);
    contactValuesRef.current = contactValues;
    const isMewsConfigured = ((_a = deal.paymentIntegration) === null || _a === void 0 ? void 0 : _a.type) === 'mews_payment';
    const dealPaymentId = (_d = (_c = (_b = deal.payments) === null || _b === void 0 ? void 0 : _b.find((payment) => payment.status === 'ACTIVE' && payment.description === 'Upsell')) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : (_f = (_e = deal.payments) === null || _e === void 0 ? void 0 : _e.find((payment) => payment.status === 'ACTIVE')) === null || _f === void 0 ? void 0 : _f.id;
    const fallbackBaseUrl = (_h = (_g = deal.paymentIntegration) === null || _g === void 0 ? void 0 : _g.checkoutBaseUrl) !== null && _h !== void 0 ? _h : null;
    const languageCode = mewsLanguageCode(language);
    useEffect(() => {
        if (!isMewsConfigured || !dealPaymentId) {
            return;
        }
        let cancelled = false;
        const query = '?id=' + encodeURIComponent(dealPaymentId);
        function confirm(paymentId) {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                setStatus('confirming');
                setMessage(null);
                for (let attempt = 0; attempt < CONFIRM_ATTEMPTS; attempt++) {
                    try {
                        const { res, data } = yield post('/mews-payment-status' + query, {
                            paymentId
                        });
                        if (cancelled) {
                            return;
                        }
                        if (res.ok && (data === null || data === void 0 ? void 0 : data.completed)) {
                            (_a = onPaidRef.current) === null || _a === void 0 ? void 0 : _a.call(onPaidRef);
                            return;
                        }
                        const notListedYet = (data === null || data === void 0 ? void 0 : data.reason) === 'NOT_FOUND' || (data === null || data === void 0 ? void 0 : data.reason) === 'NOT_CHARGED';
                        if (!notListedYet) {
                            break;
                        }
                    }
                    catch (error) {
                        // Network error: try again
                    }
                    yield wait(CONFIRM_DELAY_MS);
                }
                if (!cancelled) {
                    // The server keeps checking Mews and confirms the order by email.
                    setStatus('pending');
                    setMessage(t('Your payment is being confirmed. You will receive a confirmation email shortly.'));
                }
            });
        }
        function start() {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            return __awaiter(this, void 0, void 0, function* () {
                setStatus('loading');
                setMessage(null);
                try {
                    yield loadMewsCheckout();
                    const contact = contactValuesRef.current;
                    const { res, data } = yield post('/mews-payment-request' + query, {
                        firstName: contact === null || contact === void 0 ? void 0 : contact.firstName,
                        lastName: contact === null || contact === void 0 ? void 0 : contact.lastName,
                        phone: contact === null || contact === void 0 ? void 0 : contact.phone,
                        location: contact === null || contact === void 0 ? void 0 : contact.location
                    });
                    if (cancelled) {
                        return;
                    }
                    // Le stock est de nouveau vérifié et réservé à l'ouverture du paiement.
                    if (res.status === 409 && (data === null || data === void 0 ? void 0 : data.error) === 'OUT_OF_STOCK') {
                        (_a = onOutOfStockRef.current) === null || _a === void 0 ? void 0 : _a.call(onOutOfStockRef);
                        return;
                    }
                    if (res.status === 409 && (data === null || data === void 0 ? void 0 : data.error) === 'PRODUCT_INACTIVE') {
                        (_b = onProductInactiveRef.current) === null || _b === void 0 ? void 0 : _b.call(onProductInactiveRef);
                        return;
                    }
                    // Le code promo a pu expirer ou s'épuiser depuis l'approbation.
                    if (res.status === 409 && (data === null || data === void 0 ? void 0 : data.error) === 'PROMO_CODE_INVALID') {
                        (_c = onPromoCodeInvalidRef.current) === null || _c === void 0 ? void 0 : _c.call(onPromoCodeInvalidRef, (_d = data.reason) !== null && _d !== void 0 ? _d : null);
                        return;
                    }
                    if (data === null || data === void 0 ? void 0 : data.completed) {
                        (_e = onPaidRef.current) === null || _e === void 0 ? void 0 : _e.call(onPaidRef);
                        return;
                    }
                    if (!res.ok || !(data === null || data === void 0 ? void 0 : data.requestId) || !((_f = window.Mews) === null || _f === void 0 ? void 0 : _f.PaymentCheckout)) {
                        throw new Error((_g = data === null || data === void 0 ? void 0 : data.error) !== null && _g !== void 0 ? _g : 'MEWS_PAYMENT_REQUEST_FAILED');
                    }
                    const dataBaseUrl = (_h = data.checkoutBaseUrl) !== null && _h !== void 0 ? _h : fallbackBaseUrl;
                    window.Mews.PaymentCheckout.load(Object.assign(Object.assign({ containerId: CONTAINER_ID, requestId: data.requestId }, (dataBaseUrl ? { dataBaseUrl } : {})), { languageCode, enabledPaymentMethods: ['paymentCard', 'applePay', 'googlePay'], onSuccess: (event) => {
                            // Only a charge means the money was taken.
                            if ((event === null || event === void 0 ? void 0 : event.type) === 'payment-charged') {
                                void confirm(event.paymentId);
                            }
                        }, onFailure: (event) => {
                            console.error('Mews payment failure:', event === null || event === void 0 ? void 0 : event.type, event === null || event === void 0 ? void 0 : event.error);
                            setMessage(t('The payment failed. Please try again.'));
                        } }));
                    setStatus('ready');
                }
                catch (error) {
                    if (!cancelled) {
                        console.error('Mews checkout error:', error);
                        setStatus('error');
                        setMessage(t('Payment is not available for this order.'));
                    }
                }
            });
        }
        void start();
        return () => {
            cancelled = true;
            destroyMewsCheckout();
        };
    }, [checkoutSessionKey, dealPaymentId, isMewsConfigured, languageCode]);
    if (!isMewsConfigured) {
        return (React.createElement("p", { className: "store-mews-checkout__error" }, t('No payment method is configured.')));
    }
    if (!dealPaymentId) {
        return (React.createElement("p", { className: "store-mews-checkout__error" }, t('Payment is not available for this order.')));
    }
    return (React.createElement("div", { className: "store-mews-checkout" },
        (status === 'loading' || status === 'confirming') && (React.createElement("div", { className: "store-mews-checkout__loading" },
            React.createElement(Spinner, null),
            status === 'confirming' && (React.createElement("p", null, t('Confirming your payment…'))))),
        message && (React.createElement("p", { className: status === 'pending'
                ? 'store-mews-checkout__notice'
                : 'store-mews-checkout__error' }, message)),
        React.createElement("div", { id: CONTAINER_ID, className: 'store-mews-checkout__container' +
                (status === 'ready' ? '' : ' store-mews-checkout__container--hidden') })));
}
